import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, ConfigProvider, DatePicker, Row, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DoughnutKpiCard } from '../DoughnutKpiCard/DoughnutKpiCard';
import { StyledMachineDashboard } from './MachineDashboard.styles';
import {
  buildKpiCardConfigs,
  buildMachineUtilisationKpiConfig,
} from 'app/modules/cleaning/widgets/machine-dashboard/utils/machineDashboardKpiUtils';
import { KCCThemeGlobalStyles } from 'config/theme/kccTheme/globalStyles';
import { DependencyInjection } from 'app/cross-cutting-concerns/dependency-injection/DependencyInjection';
import { Styling } from 'app/cross-cutting-concerns/styling/Styling';
import { FleetOperatingHoursChart } from 'app/modules/cleaning/widgets/machine-dashboard/components/FleetOperatingHoursChart/FleetOperatingHoursChart';
import { MachineOperatingHoursDashboardChart } from 'app/modules/cleaning/widgets/machine-dashboard/components/MachineOperatingHoursDashboardChart/MachineOperatingHoursDashboardChart';
import { SitePerformanceChartJs } from 'app/modules/site-management/widgets/fleet-dashboard/components/SitePerformanceChartJs/SitePerformanceChartJs';
import { SiteFilter } from 'app/modules/cleaning/widgets/machine-dashboard/components/SiteFilter/SiteFilter';
import { ThemeConstants } from 'config/theme/appTheme/appTheme';
import {
  MachineDashboardProvider,
  useMachineDashboard,
} from 'app/modules/cleaning/widgets/machine-dashboard/hooks/MachineDashboardContext';
import { useOpenMachineDetailsDrawerFromUrl } from 'app/modules/machine-inventory/machine-details-panel/hooks/useOpenMachineDetailsDrawerFromUrl';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

const styling = DependencyInjection.resolve<Styling>(Styling);
const kccAntdTheme = styling.getAntDesignKCCThemeConfig();

const MachineDashboardContent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const preventCloseRef = useRef(false);
  useOpenMachineDetailsDrawerFromUrl();

  const {
    startDateCleaningWidgets,
    endDateCleaningWidgets,
    dateFilterPresets,
    fleetOperatingHoursChartData,
    isFleetOperatingHoursChartLoading,
    machineOperatingHoursDashboardChartData,
    isMachineOperatingTimeLoading,
    sitePerformanceChartData,
    isSitesLoading,
    disabledDate,
    handleDateChange,
    manualMachineDashboardData,
    isManualMachineDashboardLoading,
    isPeriodIndependentKpisLoading,
    handleSiteFilterChange,
    filterSitesData,
    noSiteMachines,
    isSiteFilterLoading,
  } = useMachineDashboard();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [activePresetLabel, setActivePresetLabel] = useState<string | null>(null);

  const showPresetLabel = !!activePresetLabel && !isPickerOpen;

  const kpiCardConfigs = useMemo(() => buildKpiCardConfigs(manualMachineDashboardData), [manualMachineDashboardData]);
  const machineUtilisationConfig = useMemo(() => {
    const alertText = activePresetLabel
      ? t('machineDashboard.machineUtilisation.alertText', { presetLabel: activePresetLabel })
      : t('machineDashboard.machineUtilisation.alertTextCustomRange', {
          startDate: dayjs(startDateCleaningWidgets).format(t('dayjsIntl.dateFormatPattern')),
          endDate: dayjs(endDateCleaningWidgets).format(t('dayjsIntl.dateFormatPattern')),
        });
    return {
      ...buildMachineUtilisationKpiConfig(manualMachineDashboardData),
      alertText,
    };
  }, [manualMachineDashboardData, activePresetLabel, startDateCleaningWidgets, endDateCleaningWidgets, t]);

  const presetsWithActiveStyle = useMemo(
    () =>
      dateFilterPresets.map(p => ({
        ...p,
        label: <span data-preset-active={p.label === activePresetLabel || undefined}>{p.label}</span>,
      })),
    [dateFilterPresets, activePresetLabel]
  );

  const findMatchingPreset = useCallback(
    (start: Dayjs, end: Dayjs) =>
      dateFilterPresets.find(p => p.value[0].isSame(start, 'day') && p.value[1].isSame(end, 'day')),
    [dateFilterPresets]
  );

  useEffect(() => {
    if (!startDateCleaningWidgets || !endDateCleaningWidgets) return;
    const matched = findMatchingPreset(dayjs(startDateCleaningWidgets), dayjs(endDateCleaningWidgets));
    setActivePresetLabel(matched?.label ?? null);
  }, [startDateCleaningWidgets, endDateCleaningWidgets, findMatchingPreset]);

  // "This" presets: onChange never fires (disabled future end date) — detect label via onCalendarChange
  const handleCalendarChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null): void => {
      const [start, end] = dates ?? [];
      if (start && end) {
        const matched = findMatchingPreset(start, end);
        if (matched) setActivePresetLabel(matched.label);
      }
    },
    [findMatchingPreset]
  );

  // "Last" presets: both dates valid → onChange fires before onOpenChange — set ref here to veto auto-close
  const handleDateChangeWithPreset = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null): void => {
      const [start, end] = dates ?? [];
      if (start && end) {
        const matched = findMatchingPreset(start, end);
        if (matched) {
          preventCloseRef.current = true;
          setActivePresetLabel(matched.label);
          setTimeout(() => {
            preventCloseRef.current = false;
          }, 0);
        } else {
          setActivePresetLabel(null);
        }
      }
      handleDateChange(dates);
    },
    [findMatchingPreset, handleDateChange]
  );

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    if (!nextOpen && preventCloseRef.current) {
      return;
    }
    setIsPickerOpen(nextOpen);
  }, []);

  return (
    <ConfigProvider
      theme={kccAntdTheme}
      input={{ className: 'kcc-theme-input' }}
      select={{ className: 'kcc-theme-select' }}
      datePicker={{ className: 'kcc-theme-date-picker' }}
      rangePicker={{ className: 'kcc-theme-date-picker' }}
      modal={{ className: 'kcc-theme-modal' }}
      card={{ className: 'kcc-theme-card' }}
      checkbox={{ className: 'kcc-theme-checkbox' }}
      tooltip={{ className: 'kcc-theme-tooltip' }}
      wave={{ disabled: true }}
    >
      <KCCThemeGlobalStyles />
      <StyledMachineDashboard className="app-layout-grey">
        <div className="machine-dashboard__header">
          <div className="machine-dashboard__header-content">
            <div className="machine-dashboard__container--wide">
              <div className="machine-dashboard__header-row">
                <h1 className="machine-dashboard__title">{t('common.machineDashboard')}</h1>
                <div className="machine-dashboard__filters-wrapper">
                  <div className="machine-dashboard__date-filter-container">
                    <DatePicker.RangePicker
                      className={`machine-dashboard__date-filter${
                        showPresetLabel ? ' machine-dashboard__date-filter--preset' : ''
                      }`}
                      popupClassName="kcc-theme-date-picker-popup"
                      presets={presetsWithActiveStyle}
                      allowClear={false}
                      open={isPickerOpen}
                      value={[dayjs(startDateCleaningWidgets), dayjs(endDateCleaningWidgets)]}
                      onCalendarChange={handleCalendarChange}
                      onChange={handleDateChangeWithPreset}
                      onOpenChange={handleOpenChange}
                      disabledDate={disabledDate}
                    />
                    {showPresetLabel && (
                      <span className="machine-dashboard__date-filter-preset-label" aria-hidden>
                        {activePresetLabel}
                      </span>
                    )}
                  </div>

                  <div className="machine-dashboard__site-filter-trigger">
                    <SiteFilter
                      sitesData={filterSitesData ?? []}
                      noSiteMachines={noSiteMachines ?? []}
                      onFilterChange={handleSiteFilterChange}
                      isLoading={isSiteFilterLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="machine-dashboard__body">
          <div className="machine-dashboard__body-content">
            <div className="machine-dashboard__container--wide">
              <div className="machine-dashboard__kpi-section">
                <div className="machine-dashboard__kpi-section-header">
                  <div className="machine-dashboard__kpi-section-title-group">
                    <p className="machine-dashboard__kpi-section-title">{t('machineDashboard.kpiSectionTitle')}</p>
                    <Tooltip
                      title={t('machineDashboard.kpiSectionDateFilterInfo')}
                      color="#1a1a1a"
                      placement="top"
                      trigger={['hover', 'click']}
                    >
                      <span className="machine-dashboard__kpi-section-info-icon">
                        <SvgIcon name="information" />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="machine-dashboard__kpi-grid">
                  {kpiCardConfigs.map(config => (
                    <DoughnutKpiCard
                      key={config.id}
                      config={config}
                      isDashboardLoading={isPeriodIndependentKpisLoading}
                    />
                  ))}
                </div>
              </div>
              <div className="machine-dashboard__fleet-section">
                <Row gutter={[ThemeConstants.BASE_GAP, ThemeConstants.BASE_GAP]}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={8}
                    className="machine-dashboard__fleet-chart-col machine-dashboard__fleet-chart-col--utilisation"
                  >
                    <DoughnutKpiCard
                      config={machineUtilisationConfig}
                      isDashboardLoading={isManualMachineDashboardLoading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12} xl={8} className="machine-dashboard__fleet-chart-col">
                    <FleetOperatingHoursChart
                      actualTotalOperatingTimeS={fleetOperatingHoursChartData?.actualTotalOperatingTimeS || 0}
                      plannedTotalOperatingTimeS={fleetOperatingHoursChartData?.plannedTotalOperatingTimeS || 0}
                      isLoading={isFleetOperatingHoursChartLoading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={8} className="machine-dashboard__fleet-chart-col">
                    <MachineOperatingHoursDashboardChart
                      data={machineOperatingHoursDashboardChartData}
                      isLoading={isMachineOperatingTimeLoading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} className="machine-dashboard__site-chart-col">
                    <SitePerformanceChartJs
                      data={sitePerformanceChartData}
                      startDate={dayjs(startDateCleaningWidgets).toISOString() ?? ''}
                      endDate={dayjs(endDateCleaningWidgets).toISOString() ?? ''}
                      isLoading={isSitesLoading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </StyledMachineDashboard>
    </ConfigProvider>
  );
};

export const MachineDashboard = (): React.JSX.Element => (
  <MachineDashboardProvider>
    <MachineDashboardContent />
  </MachineDashboardProvider>
);
